import { computed, type ComputedRef } from 'vue';
import { WeeklyOverviewTenseEnum } from '../../../shared/enums/weeklyOverviewTenseEnum';
import { weekOverviewPresenceStates } from '../../../shared/enums/weekOverviewPresenceStates.js';
import i18n from '../../../shared/locales/i18n';
import isEmpty from 'lodash/isEmpty';

interface PresenceTime {
  timestamp?: string;
  tense: WeeklyOverviewTenseEnum;
}

interface PresenceDetails {
  startTime?: PresenceTime;
  endTime?: PresenceTime;
}

export interface WeeklyOverviewActivity {
  presenceDetails?: PresenceDetails;
  type?: string;
}

export function useWeeklyOverview(activity: WeeklyOverviewActivity) {
  const t = i18n.t.bind(i18n);

  const startTime: ComputedRef<string> = computed(() => {
    if (
      activity.presenceDetails?.startTime?.tense === WeeklyOverviewTenseEnum.NOT_SPECIFIED ||
      !activity.presenceDetails?.startTime?.timestamp
    ) {
      return '';
    }
    return activity.presenceDetails.startTime.timestamp;
  });

  const endTime: ComputedRef<string> = computed(() => {
    if (
      activity.presenceDetails?.endTime?.tense === WeeklyOverviewTenseEnum.NOT_SPECIFIED ||
      !activity.presenceDetails?.endTime?.timestamp
    ) {
      return '';
    }
    return activity.presenceDetails.endTime.timestamp;
  });

  const startLabel: ComputedRef<string> = computed(() => {
    const tense = activity.presenceDetails?.startTime?.tense;
    if (!tense) {
      return '';
    }

    switch (tense) {
      case WeeklyOverviewTenseEnum.PRESENT:
        return t('presence.come');
      case WeeklyOverviewTenseEnum.PAST:
        return t('presence.came');
      default:
        return '';
    }
  });

  const endLabel: ComputedRef<string> = computed(() => {
    const tense = activity.presenceDetails?.endTime?.tense;
    if (!tense) {
      return '';
    }

    switch (tense) {
      case WeeklyOverviewTenseEnum.NOT_SPECIFIED:
      case WeeklyOverviewTenseEnum.MISSING_CHECKOUT:
        return t('presence.missing_checkout');
      case WeeklyOverviewTenseEnum.PRESENT:
        return t('presence.go');
      case WeeklyOverviewTenseEnum.PAST:
        return t('presence.went');
      default:
        return '';
    }
  });

  const blockedLabel: ComputedRef<string> = computed(() => {
    if (!activity.type) {
      return '';
    }

    switch (activity.type) {
      case weekOverviewPresenceStates.VACATION:
        return t('presence.vacation_free_time');
      case weekOverviewPresenceStates.NOT_ARRIVED:
        return t('presence.not_arrived');
      case weekOverviewPresenceStates.SICK:
        return t('presence.sick');
      default:
        return '';
    }
  });

  const hasSpecified = computed(() => !isEmpty(startTime.value || endTime.value));

  const preposition = t('label.at');

  return {
    startTime,
    endTime,
    startLabel,
    endLabel,
    blockedLabel,
    preposition,
    hasSpecified,
  };
}
